import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import usePreferStaticUI from "@/lib/usePreferStaticUI";
import { ChevronDown, Menu, X } from "lucide-react";
import { setLang } from "@/i18n";
import MagneticButton from "@/components/motion/MagneticButton";

const navItems = (t) => [
  { to: "/", label: t("nav.home") },
  { to: "/content", label: t("nav.stories") },
  { to: "/startups", label: t("nav.startups") },
  { to: "/investors", label: t("nav.investors") },
  { to: "/partners", label: t("nav.partners") },
  { to: "/about", label: t("nav.about") },
  { to: "/contact", label: t("nav.contact") },
];

const AnimatedNavLink = ({ to, label, end, testId }) => {
  const reduced = usePreferStaticUI();
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `relative text-[0.8125rem] font-medium transition-colors duration-200 group cursor-pointer ${
          isActive ? "text-raspberry" : "text-ink/75 hover:text-ink"
        }`
      }
      data-testid={testId}
    >
      {({ isActive }) => (
        <>
          {label}
          <span
            aria-hidden="true"
            className={`pointer-events-none absolute left-0 -bottom-1 h-px bg-raspberry transition-transform ease-out ${
              reduced ? "duration-0" : "duration-300"
            }`}
            style={{
              width: "100%",
              transformOrigin: "left center",
              transform: isActive ? "scaleX(1)" : "scaleX(0)",
            }}
          />
          {!isActive && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-0 -bottom-1 h-px w-full bg-raspberry origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out"
            />
          )}
        </>
      )}
    </NavLink>
  );
};

const LoginDropdown = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-[0.8125rem] font-medium text-ink/75 hover:text-ink inline-flex items-center gap-1 transition-colors duration-200 cursor-pointer"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="header-login-toggle"
      >
        {t("footer.loginGroup")}
        <ChevronDown size={14} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
            className="absolute right-0 top-full mt-3 min-w-[200px] wit-glass p-1.5 z-50"
            role="menu"
            data-testid="header-login-menu"
          >
            <Link
              to="/portal/startup/login"
              onClick={() => setOpen(false)}
              className="block px-4 py-2.5 text-sm rounded-xl text-ink hover:bg-ivory/80 transition-colors duration-200 cursor-pointer"
              role="menuitem"
              data-testid="header-login-startup"
            >
              {t("footer.loginStartup")}
            </Link>
            <Link
              to="/portal/investor/login"
              onClick={() => setOpen(false)}
              className="block px-4 py-2.5 text-sm rounded-xl text-ink hover:bg-ivory/80 transition-colors duration-200 cursor-pointer"
              role="menuitem"
              data-testid="header-login-investor"
            >
              {t("footer.loginInvestor")}
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Header = () => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const reduced = usePreferStaticUI();
  const current = i18n.language?.startsWith("en") ? "en" : "de";

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const switchLang = (lng) => {
    setLang(lng);
    setOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 pt-4 pointer-events-none" data-testid="site-header">
      <motion.div
        className={`pointer-events-auto mx-auto max-w-[76rem] rounded-2xl border transition-all duration-300 ${
          scrolled
            ? "bg-white/80 backdrop-blur-2xl border-sandBeige/60 shadow-lift py-2.5"
            : "bg-white/70 backdrop-blur-xl border-white/80 shadow-glass py-3"
        }`}
        layout
      >
        <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="font-serif text-xl sm:text-2xl text-ink tracking-tight cursor-pointer"
            data-testid="brand-link"
            onClick={() => setOpen(false)}
          >
            womenintech<span className="text-raspberry">.</span>life
          </Link>

          <nav className="hidden lg:flex items-center gap-5 xl:gap-7" data-testid="primary-nav">
            {navItems(t).map((item) => (
              <AnimatedNavLink
                key={item.to}
                to={item.to}
                label={item.label}
                end={item.to === "/"}
                testId={`nav-link-${item.to.replace("/", "") || "home"}`}
              />
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3 lg:gap-4">
            <div className="flex items-center gap-2 text-sm font-medium" data-testid="lang-toggle">
              <button
                onClick={() => switchLang("de")}
                className={`transition-colors duration-200 cursor-pointer ${current === "de" ? "text-ink" : "text-warmGrey hover:text-ink"}`}
                aria-pressed={current === "de"}
                data-testid="lang-de"
              >
                {t("lang.de")}
              </button>
              <span className="text-sandBeige">|</span>
              <button
                onClick={() => switchLang("en")}
                className={`transition-colors duration-200 cursor-pointer ${current === "en" ? "text-ink" : "text-warmGrey hover:text-ink"}`}
                aria-pressed={current === "en"}
                data-testid="lang-en"
              >
                {t("lang.en")}
              </button>
            </div>

            <LoginDropdown />

            <MagneticButton
              as="button"
              type="button"
              onClick={() => navigate("/submit-startup")}
              className="hidden lg:inline-flex items-center gap-2 bg-ink hover:bg-plum text-white rounded-full px-5 py-2 text-sm font-medium transition-colors duration-300 whitespace-nowrap cursor-pointer"
              data-testid="header-cta-submit"
              strength={0.3}
              maxOffset={6}
            >
              {t("nav.submitPitch")}
            </MagneticButton>
          </div>

          <button
            className="md:hidden p-2 text-ink cursor-pointer"
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? t("nav.menuClose") : t("nav.menuOpen")}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </motion.div>

      <AnimatePresence>
        {open && (
          <motion.div
            className="md:hidden fixed inset-0 z-50 pointer-events-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduced ? 0 : 0.2 }}
            data-testid="mobile-menu"
          >
            <motion.div
              className="absolute inset-0 bg-ink/30 backdrop-blur-sm"
              onClick={() => setOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              aria-hidden="true"
            />
            <motion.aside
              className="absolute right-0 top-0 h-screen w-[88%] max-w-sm wit-glass-strong border-l border-sandBeige/60 flex flex-col"
              initial={reduced ? { x: 0 } : { x: "100%" }}
              animate={{ x: 0 }}
              exit={reduced ? { x: 0 } : { x: "100%" }}
              transition={{ type: "spring", stiffness: 380, damping: 36 }}
            >
              <div className="flex items-center justify-between px-6 pt-6">
                <span className="font-serif text-xl text-ink">womenintech<span className="text-raspberry">.</span>life</span>
                <button type="button" onClick={() => setOpen(false)} aria-label={t("nav.menuClose")} className="p-2 text-ink cursor-pointer">
                  <X size={20} />
                </button>
              </div>
              <motion.nav
                className="flex-1 px-6 py-8 flex flex-col gap-1"
                initial="hidden"
                animate="show"
                variants={{
                  hidden: {},
                  show: { transition: { staggerChildren: reduced ? 0 : 0.05, delayChildren: 0.05 } },
                }}
              >
                {navItems(t).map((item) => (
                  <motion.div
                    key={item.to}
                    variants={{
                      hidden: { opacity: 0, x: 18 },
                      show: { opacity: 1, x: 0, transition: { duration: 0.4, ease: [0.22, 0.61, 0.36, 1] } },
                    }}
                  >
                    <NavLink
                      to={item.to}
                      end={item.to === "/"}
                      onClick={() => setOpen(false)}
                      className={({ isActive }) =>
                        `block py-3 text-2xl font-serif transition-colors duration-200 cursor-pointer ${isActive ? "text-raspberry" : "text-ink hover:text-raspberry"}`
                      }
                      data-testid={`mobile-nav-${item.to.replace("/", "") || "home"}`}
                    >
                      {item.label}
                    </NavLink>
                  </motion.div>
                ))}
              </motion.nav>
              <div className="px-6 pb-8 flex flex-col gap-4 border-t border-sandBeige/60 pt-6">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <button onClick={() => switchLang("de")} className={`cursor-pointer ${current === "de" ? "text-ink" : "text-warmGrey"}`} data-testid="mobile-lang-de">{t("lang.de")}</button>
                  <span className="text-sandBeige">|</span>
                  <button onClick={() => switchLang("en")} className={`cursor-pointer ${current === "en" ? "text-ink" : "text-warmGrey"}`} data-testid="mobile-lang-en">{t("lang.en")}</button>
                </div>
                <div className="flex flex-col gap-2 text-sm">
                  <p className="wit-eyebrow">{t("footer.loginGroup")}</p>
                  <Link to="/portal/startup/login" onClick={() => setOpen(false)} className="text-ink hover:text-raspberry transition-colors" data-testid="mobile-login-startup">
                    {t("footer.loginStartup")}
                  </Link>
                  <Link to="/portal/investor/login" onClick={() => setOpen(false)} className="text-ink hover:text-raspberry transition-colors" data-testid="mobile-login-investor">
                    {t("footer.loginInvestor")}
                  </Link>
                </div>
                <Link to="/submit-startup" onClick={() => setOpen(false)} className="wit-btn-primary justify-center" data-testid="mobile-cta-submit">
                  {t("nav.submitPitch")}
                </Link>
              </div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
